import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const installations = [
  {
    src: 'https://storage.readdy-site.link/project_files/b3dbeb0b-301d-4a9e-9856-b33dec5d7a2e/0b031d93-8468-4358-8abf-0215940f0ce1_Ministerios-Nueva-Esperanza.png?v=8e9b40ede0774de3968fd6a12971d27b',
    church: 'Ministerios Nueva Esperanza',
    city: 'USA',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/b3dbeb0b-301d-4a9e-9856-b33dec5d7a2e/088ef023-4d32-419d-bdd8-eb8538dca4c9_Iglesia-Embajadores-del-Gran-Rey--NY.png?v=21a5cbfaae90f98ae530c3b0bbd6a7a7',
    church: 'Iglesia Embajadores del Gran Rey',
    city: 'New York, NY',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/b3dbeb0b-301d-4a9e-9856-b33dec5d7a2e/a86ac3cb-a41c-4f26-8acf-5c934b8927e5_Maranatha-Centro-de-Adoracion.png?v=4f972796046a5f3cda2fcc4845668527',
    church: 'Maranatha Centro de Adoración',
    city: 'USA',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/b3dbeb0b-301d-4a9e-9856-b33dec5d7a2e/d801a79a-6c19-4a64-b76a-ff70bb480e15_Casa-de-Adoracion-A-Las-Naciones-PR.png?v=19b6d60a967e65775e7502ea329e7cc3',
    church: 'Casa de Adoración A Las Naciones',
    city: 'Puerto Rico',
  },
];

export default function GallerySection() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="gallery" ref={ref} className="bg-brand-black py-20 md:py-32 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-gold" />
            <span className="font-body text-xs text-gold tracking-widest uppercase font-semibold">
              {t('gallery.badge')}
            </span>
            <div className="w-8 h-px bg-gold" />
          </div>
          <h2 className="font-heading font-black text-3xl md:text-5xl text-brand-white leading-tight">
            {t('gallery.title1')}
            <br />
            <span className="text-gold">{t('gallery.title2')}</span>
          </h2>
          <p className="font-body text-sm md:text-base text-brand-white/50 max-w-xl mx-auto mt-4 leading-relaxed">
            {t('gallery.sub')}
          </p>
        </div>

        {/* Mobile: single column stack */}
        <div className="flex flex-col gap-3 md:hidden">
          {installations.map((item, i) => (
            <div
              key={i}
              className="relative group overflow-hidden rounded-2xl cursor-pointer"
              style={{ height: '240px' }}
              onClick={() => setActive(i)}
            >
              <img
                src={item.src}
                alt={item.church}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="font-heading font-bold text-sm text-brand-white leading-tight">{item.church}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xs text-gold" />
                  </div>
                  <span className="font-body text-xs text-brand-white/70">{item.city}</span>
                </div>
              </div>
              <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gold/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <i className="ri-zoom-in-line text-sm text-brand-black" />
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: mosaic grid */}
        <div
          className={`hidden md:grid md:grid-cols-3 gap-4 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ gridTemplateRows: 'auto auto' }}
        >
          {/* Big card — spans 2 cols and 2 rows */}
          <div
            className="relative group overflow-hidden rounded-2xl cursor-pointer md:col-span-2 md:row-span-2"
            style={{ minHeight: '420px' }}
            onClick={() => setActive(0)}
          >
            <img
              src={installations[0].src}
              alt={installations[0].church}
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-black/80 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-brand-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <p className="font-heading font-bold text-base text-brand-white">{installations[0].church}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-xs text-gold" />
                </div>
                <span className="font-body text-xs text-brand-white/70">{installations[0].city}</span>
              </div>
            </div>
            <div className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-gold/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <i className="ri-zoom-in-line text-base text-brand-black" />
            </div>
          </div>

          {/* Small cards — col 3, rows 1 and 2 */}
          {[installations[1], installations[2]].map((item, i) => (
            <div
              key={i}
              className="relative group overflow-hidden rounded-2xl cursor-pointer"
              style={{ minHeight: '200px' }}
              onClick={() => setActive(i + 1)}
            >
              <img
                src={item.src}
                alt={item.church}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-black/80 via-transparent to-transparent" />
              <div className="absolute inset-0 bg-brand-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="font-heading font-bold text-sm text-brand-white leading-tight">{item.church}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xs text-gold" />
                  </div>
                  <span className="font-body text-xs text-brand-white/70">{item.city}</span>
                </div>
              </div>
              <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gold/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <i className="ri-zoom-in-line text-sm text-brand-black" />
              </div>
            </div>
          ))}

          {/* Bottom wide card — spans 3 cols */}
          <div
            className="relative group overflow-hidden rounded-2xl cursor-pointer md:col-span-3"
            style={{ minHeight: '220px' }}
            onClick={() => setActive(3)}
          >
            <img
              src={installations[3].src}
              alt={installations[3].church}
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-black/80 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-brand-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <p className="font-heading font-bold text-base text-brand-white">{installations[3].church}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-xs text-gold" />
                </div>
                <span className="font-body text-xs text-brand-white/70">{installations[3].city}</span>
              </div>
            </div>
            <div className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-gold/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <i className="ri-zoom-in-line text-base text-brand-black" />
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className={`text-center mt-10 md:mt-12 transition-all duration-700 delay-400 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="font-body text-sm text-brand-white/50 mb-4">{t('gallery.cta.text')}</p>
          <button
            onClick={scrollToContact}
            className="inline-flex items-center gap-2 font-heading font-bold text-sm border border-gold/50 text-gold px-8 py-3 rounded-full hover:bg-gold hover:text-brand-black transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </div>
            {t('gallery.cta.button')}
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {active !== null && (
        <div
          className="fixed inset-0 z-50 bg-brand-black/95 flex items-center justify-center p-4 md:p-8"
          onClick={() => setActive(null)}
        >
          <div
            className="relative max-w-4xl w-full rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={installations[active].src}
              alt={installations[active].church}
              className="w-full h-auto object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-black/95 to-transparent p-5 md:p-6">
              <p className="font-heading font-bold text-lg md:text-xl text-brand-white">{installations[active].church}</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-xs text-gold" />
                </div>
                <span className="font-body text-sm text-brand-white/70">{installations[active].city}</span>
              </div>
            </div>
            <button
              onClick={() => setActive(null)}
              className="absolute top-3 right-3 md:top-4 md:right-4 w-9 h-9 flex items-center justify-center rounded-full bg-brand-black/70 border border-brand-white/20 text-brand-white hover:border-gold hover:text-gold transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
